import React, { useState, useEffect } from 'react';
import { Competition } from '../models';
import { SimilarityType } from '../services/DataService';
import { LocalStorageDataService } from '../services/LocalStorageDataService';
import './SimilaritySearch.css';

interface SimilaritySearchProps {
  onSimilarCompetitionsFound: (competitions: Competition[]) => void;
}

const SimilaritySearch: React.FC<SimilaritySearchProps> = ({ onSimilarCompetitionsFound }) => {
  const [competitionSlug, setCompetitionSlug] = useState('');
  const [similarityType, setSimilarityType] = useState<SimilarityType>(SimilarityType.ALL);
  const [isLoading, setIsLoading] = useState(false);
  const [availableCompetitions, setAvailableCompetitions] = useState<Competition[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const dataService = new LocalStorageDataService();
  
  // 利用可能なコンペティションを取得
  useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        const competitions = await dataService.getCompetitions();
        setAvailableCompetitions(competitions);
      } catch (err) {
        console.error('コンペティションの取得中にエラーが発生しました:', err);
        setError('コンペティションの取得に失敗しました');
      }
    };
    
    fetchCompetitions();
  }, []);
  
  const handleSearch = async () => {
    if (!competitionSlug) {
      setError('コンペティションSlugを入力してください');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const similarCompetitions = await dataService.findSimilarCompetitions(
        competitionSlug,
        similarityType
      );
      
      onSimilarCompetitionsFound(similarCompetitions);
      
      if (similarCompetitions.length === 0) {
        setError('類似するコンペティションが見つかりませんでした');
      }
    } catch (err) {
      console.error('類似コンペの検索中にエラーが発生しました:', err);
      setError('類似コンペの検索に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="similarity-search">
      <h2>類似コンペティション検索</h2>
      
      <div className="form-group">
        <label htmlFor="competitionSlug">コンペティションSlug:</label>
        <select
          id="competitionSlug"
          value={competitionSlug}
          onChange={(e) => setCompetitionSlug(e.target.value)}
        >
          <option value="">選択してください</option>
          {availableCompetitions.map((comp) => (
            <option key={comp.slug} value={comp.slug}>
              {comp.title} ({comp.slug})
            </option>
          ))}
        </select>
      </div>
      
      <div className="form-group">
        <label>類似性の観点:</label>
        <div className="radio-group">
          <label>
            <input
              type="radio"
              name="similarityType"
              checked={similarityType === SimilarityType.TITLE}
              onChange={() => setSimilarityType(SimilarityType.TITLE)}
            />
            タイトル
          </label>
          
          <label>
            <input
              type="radio"
              name="similarityType"
              checked={similarityType === SimilarityType.TAGS}
              onChange={() => setSimilarityType(SimilarityType.TAGS)}
            />
            タグ
          </label>
          
          <label>
            <input
              type="radio"
              name="similarityType"
              checked={similarityType === SimilarityType.DATA_DOMAIN}
              onChange={() => setSimilarityType(SimilarityType.DATA_DOMAIN)}
            />
            データドメイン
          </label>
          
          <label>
            <input
              type="radio"
              name="similarityType"
              checked={similarityType === SimilarityType.DESCRIPTION}
              onChange={() => setSimilarityType(SimilarityType.DESCRIPTION)}
            />
            説明
          </label>
          
          <label>
            <input
              type="radio"
              name="similarityType"
              checked={similarityType === SimilarityType.ALL}
              onChange={() => setSimilarityType(SimilarityType.ALL)}
            />
            すべて
          </label>
        </div>
      </div>
      
      <button 
        className="search-button"
        onClick={handleSearch}
        disabled={isLoading || !competitionSlug}
      >
        {isLoading ? '検索中...' : '類似コンペを検索'}
      </button>
      
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default SimilaritySearch;
